import {
  Alert,
  Badge,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { isNotEmpty, useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconChevronDown,
  IconInfoCircle,
  IconSettings,
  IconTrash,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type { Day, ScheduleInput, ScheduleOutput } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { Header } from "@/components/Header";
import {
  convertTimeToString,
  daysOptions,
  parseTimeStringToNumber,
} from "@/pages/trainer/profile/common";
import { TrainerRoutes, useNavigator } from "@/routers/routes";

// Reusable default day object
export const DEFAULT_DAY = { startTime: "09:00", endTime: "17:00" };

type TimeRange = {
  startTime: string;
  endTime: string;
};

type ScheduleDayTimeRanges = {
  [K in Day]?: TimeRange;
};

type TimeRangeNumber = {
  startTime: number;
  endTime: number;
};

type ScheduleDayTimeRangesNumber = {
  [K in Day]?: TimeRangeNumber;
};

// Utility functions for per-day time ranges (now using objects)
const hasSameTimeRanges = (dayTimeRanges: ScheduleDayTimeRanges) => {
  const ranges = Object.values(dayTimeRanges);
  if (!ranges || ranges.length <= 1 || !ranges[0]) return true;

  const first = ranges[0];
  return ranges.every(
    (range) =>
      range.startTime === first.startTime && range.endTime === first.endTime,
  );
};

const createDayTimeRangesFromUniform = (
  dayTimeRanges: ScheduleDayTimeRanges,
  startTime: string,
  endTime: string,
) => {
  const startTimeNum = parseTimeStringToNumber(startTime);
  const endTimeNum = parseTimeStringToNumber(endTime);

  const result: ScheduleDayTimeRangesNumber = {};
  for (const day of Object.keys(dayTimeRanges) as Day[]) {
    result[day] = {
      startTime: startTimeNum,
      endTime: endTimeNum,
    };
  }
  return result;
};

const createUniformTimeFromDayRanges = (
  dayTimeRanges: ScheduleDayTimeRangesNumber,
) => {
  const ranges = Object.values(dayTimeRanges);
  if (!ranges || ranges.length === 0 || !ranges[0]) {
    return DEFAULT_DAY;
  }

  const first = ranges[0];
  return {
    startTime: convertTimeToString(first.startTime),
    endTime: convertTimeToString(first.endTime),
  };
};

interface ScheduleEditFormProps {
  schedule?: ScheduleOutput;
  schedules: ScheduleOutput[];
  sessionTypes: { id: string; name: string; scheduleId: string }[];
}

const ScheduleEditForm = ({
  schedule,
  schedules,
  sessionTypes,
}: ScheduleEditFormProps) => {
  const { navigate } = useNavigator();
  const [submitting, setSubmitting] = useState(false);
  const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);
  const [advancedOpen, { toggle: toggleAdvanced }] = useDisclosure(false);

  // Detect if schedule uses same time ranges for all days
  const usesSameTimeRanges = useMemo(() => {
    if (!schedule) return true;
    if (Object.keys(schedule.dayTimeRanges).length > 0) {
      return hasSameTimeRanges(schedule.dayTimeRanges);
    }
  }, [schedule]);

  const [useSameTimeForAllDays, setUseSameTimeForAllDays] =
    useState(usesSameTimeRanges);

  const updateSchedulesMutation = client.useMutation(
    "post",
    "/trainer/profile/schedules",
  );

  const associatedSessionTypes = schedule
    ? sessionTypes.filter((st) => st.scheduleId === schedule.id)
    : [];
  const canDelete = associatedSessionTypes.length === 0;

  const defaultValues = useMemo(
    () => ({
      id: crypto.randomUUID(),
      name: "",
      startWorkTime: DEFAULT_DAY.startTime,
      endWorkTime: DEFAULT_DAY.endTime,
      dayTimeRanges: {
        MON: DEFAULT_DAY,
        TUE: DEFAULT_DAY,
        WED: DEFAULT_DAY,
        THU: DEFAULT_DAY,
        FRI: DEFAULT_DAY,
      } as Record<Day, { startTime: string; endTime: string }>,
      stepMinutes: "30",
      minGapMinutes: 15,
      minDaysAhead: 1,
      maxDaysAhead: 28,
    }),
    [],
  );

  const initialValues = useMemo(() => {
    if (!schedule) return undefined;
    // Initialize form based on whether schedule has per-day ranges
    const uniformTime = createUniformTimeFromDayRanges(schedule.dayTimeRanges);

    // Convert from API format to form format
    const formDayTimeRanges: ScheduleDayTimeRanges = {};
    for (const [day, timeRange] of Object.entries(schedule.dayTimeRanges)) {
      formDayTimeRanges[day as Day] = {
        startTime: convertTimeToString(timeRange.startTime),
        endTime: convertTimeToString(timeRange.endTime),
      };
    }

    return {
      id: schedule.id,
      name: schedule.name,
      startWorkTime: uniformTime.startTime,
      endWorkTime: uniformTime.endTime,
      stepMinutes: schedule.stepMinutes.toString(),
      minGapMinutes: schedule.minGapMinutes,
      minDaysAhead: schedule.minDaysAhead,
      maxDaysAhead: schedule.maxDaysAhead,
      dayTimeRanges: formDayTimeRanges,
    };
  }, [schedule]);

  const form = useForm({
    initialValues: initialValues ?? defaultValues,
    transformValues: (values) => {
      // Convert dayTimeRanges object to API format
      const dayTimeRangesForAPI: ScheduleDayTimeRangesNumber = {};
      for (const [day, timeRange] of Object.entries(values.dayTimeRanges) as [
        Day,
        TimeRange,
      ][]) {
        dayTimeRangesForAPI[day] = {
          startTime: parseTimeStringToNumber(timeRange.startTime),
          endTime: parseTimeStringToNumber(timeRange.endTime),
        };
      }

      return {
        id: values.id,
        name: values.name,
        stepMinutes: parseInt(values.stepMinutes),
        minGapMinutes: values.minGapMinutes,
        minDaysAhead: values.minDaysAhead,
        maxDaysAhead: values.maxDaysAhead,
        dayTimeRanges: dayTimeRangesForAPI,
      };
    },
    validate: {
      name: isNotEmpty("Schedule name is required"),
      startWorkTime: (value) => {
        if (useSameTimeForAllDays) {
          return !value ? "Start work time is required" : null;
        }
        return null; // Skip validation for per-day mode
      },
      endWorkTime: (value, values) => {
        if (useSameTimeForAllDays) {
          if (!value) return "End work time is required";
          const startTime = parseTimeStringToNumber(values.startWorkTime);
          const endTime = parseTimeStringToNumber(value);
          if (startTime >= endTime) {
            return "End time must be after start time";
          }
        }
        return null; // Skip validation for per-day mode
      },
      dayTimeRanges: (value) => {
        // Always validate day time ranges since they define working days
        if (!value || Object.keys(value).length === 0) {
          return "At least one working day is required";
        }

        // Check each day has valid time ranges
        for (const [day, timeRange] of Object.entries(value)) {
          if (!timeRange.startTime || !timeRange.endTime) {
            return "All days must have both start and end times";
          }

          const startTime = parseTimeStringToNumber(timeRange.startTime);
          const endTime = parseTimeStringToNumber(timeRange.endTime);
          if (startTime >= endTime) {
            const dayLabel =
              daysOptions.find((d) => d.value === day)?.label || day;
            return `End time must be after start time for ${dayLabel}`;
          }
        }
        return null;
      },
      stepMinutes: isNotEmpty("Step minutes is required"),
      minGapMinutes: (value) =>
        value < 0 ? "Minimum gap must be 0 or greater" : null,
      minDaysAhead: (value) =>
        value < 0 ? "Minimum days ahead must be 0 or greater" : null,
      maxDaysAhead: (value, values) =>
        value < values.minDaysAhead
          ? "Maximum days ahead must be greater than minimum days ahead"
          : null,
    },
  });

  const handleSubmit = async (values: ScheduleInput) => {
    setSubmitting(true);

    // Values are already transformed by Mantine's form.onSubmit
    // Prepare the schedule data based on the mode
    const scheduleData = {
      id: values.id,
      name: values.name,
      stepMinutes: values.stepMinutes,
      minGapMinutes: values.minGapMinutes,
      minDaysAhead: values.minDaysAhead,
      maxDaysAhead: values.maxDaysAhead,
      dayTimeRanges: {},
    };

    if (useSameTimeForAllDays) {
      // Use uniform time ranges - create dayTimeRanges with same time for all selected days
      const formDayRanges = form.values.dayTimeRanges; // Use untransformed form values
      scheduleData.dayTimeRanges = createDayTimeRangesFromUniform(
        formDayRanges,
        form.values.startWorkTime,
        form.values.endWorkTime,
      );
    } else {
      // Use per-day time ranges (already transformed)
      scheduleData.dayTimeRanges = values.dayTimeRanges;
    }

    const updatedSchedules = schedule
      ? schedules.map((s) => (s.id === scheduleData.id ? scheduleData : s))
      : [...schedules, scheduleData];

    await updateSchedulesMutation.mutateAsync({ body: updatedSchedules });
    navigate({ path: TrainerRoutes.PROFILE.SCHEDULES });
  };

  const handleDelete = async () => {
    if (!canDelete || !schedule) return;

    const updatedSchedules = schedules.filter((s) => s.id !== schedule.id);
    await updateSchedulesMutation.mutateAsync({ body: updatedSchedules });
    navigate({ path: TrainerRoutes.PROFILE.SCHEDULES });
  };

  const handleCancel = () => {
    navigate({ path: TrainerRoutes.PROFILE.SCHEDULES });
  };

  const errorList = useMemo(() => Object.entries(form.errors), [form.errors]);

  return (
    <Stack gap="md">
      <Header
        title={schedule ? "Edit Schedule" : "Create New Schedule"}
        actionControl={
          schedule && (
            <Button
              color="red"
              variant="outline"
              leftSection={<IconTrash size={16} />}
              onClick={openDeleteModal}
              disabled={!canDelete || submitting}
            >
              Delete
            </Button>
          )
        }
      />

      {associatedSessionTypes.length > 0 && (
        <Alert color="blue" icon={<IconInfoCircle size={16} />}>
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              This schedule is used by {associatedSessionTypes.length} session
              type{associatedSessionTypes.length === 1 ? "" : "s"}:
            </Text>
            <Group gap="xs">
              {associatedSessionTypes.map((sessionType) => (
                <Badge key={sessionType.id} variant="light" size="sm">
                  {sessionType.name}
                </Badge>
              ))}
            </Group>
          </Stack>
        </Alert>
      )}

      {!canDelete && schedule && (
        <Alert color="orange" icon={<IconInfoCircle size={16} />}>
          This schedule cannot be deleted because it is currently used by
          session types. Please reassign these session types to different
          schedules before deleting this one.
        </Alert>
      )}

      <Card withBorder>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack gap="md">
            {errorList.length > 0 && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {errorList.map(([key, error]) => (
                  <Text key={key}>{error}</Text>
                ))}
              </Alert>
            )}

            <TextInput
              label="Schedule Name"
              placeholder="e.g., Morning Schedule, Weekend Hours"
              {...form.getInputProps("name")}
            />

            <Stack gap="xs">
              <Text size="sm" fw={500}>
                Working Days
              </Text>
              <Text size="xs" c="dimmed">
                Select the days you want to work by checking them below
              </Text>
              <Group>
                {daysOptions.map((day) => {
                  const isSelected = day.value in form.values.dayTimeRanges;
                  return (
                    <Chip
                      key={day.value}
                      checked={isSelected}
                      onChange={(checked) => {
                        const currentRanges = { ...form.values.dayTimeRanges };
                        if (checked) {
                          // Add day with default times
                          currentRanges[day.value] = {
                            startTime:
                              form.values.startWorkTime ||
                              DEFAULT_DAY.startTime,
                            endTime:
                              form.values.endWorkTime || DEFAULT_DAY.endTime,
                          };
                        } else {
                          // Remove day
                          delete currentRanges[day.value];
                        }
                        form.setFieldValue("dayTimeRanges", currentRanges);
                      }}
                    >
                      {day.label}
                    </Chip>
                  );
                })}
              </Group>
              {Object.keys(form.values.dayTimeRanges).length === 0 && (
                <Text c="var(--mantine-color-error)" size="xs">
                  At least one working day is required
                </Text>
              )}
            </Stack>

            <Divider label="Working Hours" labelPosition="center" />

            <Switch
              label="Use the same working hours for all days"
              checked={useSameTimeForAllDays}
              onChange={(event) => {
                const newSameTimeForAllDays = event.currentTarget.checked;
                setUseSameTimeForAllDays(newSameTimeForAllDays);

                // If switching to same time, sync all day ranges with uniform time
                if (!newSameTimeForAllDays) {
                  return;
                }
                const updatedRanges = { ...form.values.dayTimeRanges };
                for (const day of Object.keys(updatedRanges) as Day[]) {
                  updatedRanges[day] = {
                    startTime: form.values.startWorkTime,
                    endTime: form.values.endWorkTime,
                  };
                }
                form.setFieldValue("dayTimeRanges", updatedRanges);
              }}
            />

            {useSameTimeForAllDays ? (
              <Group grow>
                <TimeInput
                  label="Start Work Time"
                  {...form.getInputProps("startWorkTime")}
                />
                <TimeInput
                  label="End Work Time"
                  {...form.getInputProps("endWorkTime")}
                />
              </Group>
            ) : (
              <Stack gap="sm">
                <Text size="sm" fw={500}>
                  Working Hours by Day
                </Text>
                {Object.entries(form.values.dayTimeRanges).map(
                  ([day, timeRange]) => {
                    const dayLabel =
                      daysOptions.find((d) => d.value === day)?.label || day;

                    return (
                      <Group key={day} align="center" grow>
                        <Text size="sm" fw={500}>
                          {dayLabel}
                        </Text>
                        <TimeInput
                          label="Start"
                          value={timeRange.startTime}
                          onChange={(e) => {
                            const newDayRanges = {
                              ...form.values.dayTimeRanges,
                              [day]: {
                                ...timeRange,
                                startTime: e.target.value,
                              },
                            };
                            form.setFieldValue("dayTimeRanges", newDayRanges);
                          }}
                        />
                        <TimeInput
                          label="End"
                          value={timeRange.endTime}
                          onChange={(e) => {
                            const newDayRanges = {
                              ...form.values.dayTimeRanges,
                              [day]: {
                                ...timeRange,
                                endTime: e.target.value,
                              },
                            };
                            form.setFieldValue("dayTimeRanges", newDayRanges);
                          }}
                        />
                      </Group>
                    );
                  },
                )}
              </Stack>
            )}

            <Button
              variant="subtle"
              leftSection={<IconSettings size={16} />}
              rightSection={
                <IconChevronDown
                  size={16}
                  style={{
                    transform: advancedOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              }
              onClick={toggleAdvanced}
              fullWidth
              justify="space-between"
            >
              Advanced Settings
            </Button>

            <Collapse in={advancedOpen}>
              <Stack gap="md" pt="sm">
                <Select
                  label="Time Slot Step (in minutes)"
                  data={[
                    { label: "15 minutes", value: "15" },
                    { label: "30 minutes (recommended)", value: "30" },
                    { label: "60 minutes", value: "60" },
                  ]}
                  {...form.getInputProps("stepMinutes")}
                />

                <NumberInput
                  label="Minimum Gap Between Sessions (in minutes)"
                  min={0}
                  {...form.getInputProps("minGapMinutes")}
                />

                <NumberInput
                  label="Minimum Days From Today to Book a Session"
                  min={0}
                  {...form.getInputProps("minDaysAhead")}
                />

                <NumberInput
                  label="Maximum Days From Today to Book a Session"
                  min={1}
                  {...form.getInputProps("maxDaysAhead")}
                />
              </Stack>
            </Collapse>

            <Group justify="flex-end" gap="md">
              <Button
                variant="default"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button type="submit" loading={submitting}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        </form>
      </Card>

      <Modal
        opened={deleteModalOpen}
        onClose={closeDeleteModal}
        title="Delete Schedule"
      >
        <Stack gap="md">
          <Text>
            Are you sure you want to delete "{schedule?.name}"? This action
            cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button
              variant="outline"
              onClick={closeDeleteModal}
              disabled={updateSchedulesMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={updateSchedulesMutation.isPending}
            >
              Delete Schedule
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
};

export const ScheduleEdit = () => {
  const { scheduleId } = useParams();
  const { navigate } = useNavigator();
  const isNew = scheduleId === "new";

  const { data: schedules } = client.useQuery(
    "get",
    "/trainer/profile/schedules",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  const { data: sessionTypes } = client.useQuery(
    "get",
    "/trainer/profile/session-types",
    undefined,
    { staleTime: 0, gcTime: 0 },
  );

  if (!schedules || !sessionTypes) {
    return <FancyLoader />;
  }

  const existingSchedule = schedules.find((s) => s.id === scheduleId);

  if (!isNew && !existingSchedule) {
    return (
      <Stack>
        <Header title="Schedule Not Found" />
        <Text>The requested schedule could not be found.</Text>
        <Button
          variant="outline"
          onClick={() => navigate({ path: TrainerRoutes.PROFILE.SCHEDULES })}
        >
          Back to Schedules
        </Button>
      </Stack>
    );
  }

  return (
    <ScheduleEditForm
      schedule={existingSchedule}
      schedules={schedules}
      sessionTypes={sessionTypes}
    />
  );
};
